import React, { Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { ThemeProvider } from '@shared/contexts/ThemeContext';
import { ViewportProvider } from '@shared/contexts/ViewportContext';
import { ToastProvider } from '@shared/contexts/ToastContext';
import { ConfirmProvider, UnsavedChangesProvider, lazyWithReload } from '@shared';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { RegionProvider } from '@/contexts/RegionContext';
import MainLayout from '@/components/layout/MainLayout';
import { rememberReturnTo } from '@/lib/returnTo';
import { legacyRedirect } from '@/lib/legacyRoutes';

/* Lazy-loaded pages — `lazyWithReload` auto-recovers from stale-chunk errors
 * after a deploy (old tab references a chunk hash that no longer exists). */
const LoginPage = lazyWithReload(() => import('@/pages/LoginPage'));
const SessionBridgePage = lazyWithReload(() => import('@/pages/SessionBridgePage'));
const DashboardPage = lazyWithReload(() => import('@/pages/DashboardPage'));
const TalleresPage = lazyWithReload(() => import('@/pages/TalleresPage'));
const TallerDetailPage = lazyWithReload(() => import('@/pages/TallerDetailPage'));
const MigracionesPage = lazyWithReload(() => import('@/pages/migraciones/MigracionesPage'));
const MigracionDetailPage = lazyWithReload(() => import('@/pages/migraciones/MigracionDetailPage'));
const UsuariosPage = lazyWithReload(() => import('@/pages/UsuariosPage'));
const UsuarioDetailPage = lazyWithReload(() => import('@/pages/UsuarioDetailPage'));
const SuscripcionesPage = lazyWithReload(() => import('@/pages/SuscripcionesPage'));
const PlanesHubPage = lazyWithReload(() => import('@/pages/PlanesHubPage'));
const NotFoundPage = lazyWithReload(() => import('@/pages/NotFoundPage'));
const FinancieroPage = lazyWithReload(() => import('@/pages/FinancieroPage'));
const FinancieroGastoDetailPage = lazyWithReload(() => import('@/pages/FinancieroGastoDetailPage'));
const FinancieroProveedorDetailPage = lazyWithReload(() => import('@/pages/FinancieroProveedorDetailPage'));
const CatalogoPage = lazyWithReload(() => import('@/pages/CatalogoPage'));
const AnalyticsPage = lazyWithReload(() => import('@/pages/AnalyticsPage'));
const MonitorPage = lazyWithReload(() => import('@/pages/MonitorPage'));
const AlarmDetailPage = lazyWithReload(() => import('@/pages/AlarmDetailPage'));
const TraduccionesPage = lazyWithReload(() => import('@/pages/TraduccionesPage'));
const EmailPage = lazyWithReload(() => import('@/pages/EmailPage'));
const AlmacenamientoPage = lazyWithReload(() => import('@/pages/AlmacenamientoPage'));
const ConfiguracionPage = lazyWithReload(() => import('@/pages/ConfiguracionPage'));
const MiCuentaPage = lazyWithReload(() => import('@/pages/MiCuentaPage'));
const DesarrolloPage = lazyWithReload(() => import('@/pages/DesarrolloPage'));
const TestPage = lazyWithReload(() => import('@/pages/TestPage'));
const ProcesosPage = lazyWithReload(() => import('@/pages/procesos/ProcesosPage'));
const PaisesPage = lazyWithReload(() => import('@/pages/paises/PaisesPage'));
const PaisDetailPage = lazyWithReload(() => import('@/pages/paises/PaisDetailPage'));
const ImpresorasPage = lazyWithReload(() => import('@/pages/impresoras/ImpresorasPage'));
const VersionesAgentePage = lazyWithReload(() => import('@/pages/impresoras/VersionesAgentePage'));
const SoportePage = lazyWithReload(() => import('@/pages/SoportePage'));
const PartnersPage = lazyWithReload(() => import('@/pages/PartnersPage'));
const PartnerDetailPage = lazyWithReload(() => import('@/pages/PartnerDetailPage'));

function Loading() {
  return (
    <div className="flex h-screen items-center justify-center bg-background-light dark:bg-background-dark">
      <div className="text-text-sub dark:text-slate-400">Cargando…</div>
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!user || !user.is_platform_admin) {
    // Un enlace profundo (el «Abrir» de un aviso de alarma) vuelve a su sitio
    // tras el login: lo leen LoginPage / SessionBridgePage (`lib/returnTo.ts`).
    rememberReturnTo(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

/** Root layout route: hosts the Suspense boundary for the lazy route tree.
 *  Needed because the data router takes a static route tree. */
function RootGate() {
  // Una dirección vieja (un enlace de alarma guardado, un marcador) se lleva a
  // la de hoy antes de pintar nada: así las páginas no llevan alias propios y
  // el «volver tras el login» guarda ya la dirección buena.
  const location = useLocation();
  const target = legacyRedirect(location.pathname, location.search);
  if (target) return <Navigate to={target + location.hash} replace />;
  return (
    <Suspense fallback={<Loading />}>
      <Outlet />
    </Suspense>
  );
}

/** Data router — same route tree as before, expressed declaratively so
 *  `useBlocker` (unsaved-changes guard) works. Defined once at module scope. */
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootGate />}>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/session-bridge" element={<SessionBridgePage />} />
      <Route
        element={
          <RequireAuth>
            <MainLayout />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="talleres" element={<TalleresPage />} />
        <Route path="talleres/:id" element={<TallerDetailPage />} />
        <Route path="migraciones" element={<MigracionesPage />} />
        <Route path="migraciones/:runUuid" element={<MigracionDetailPage />} />
        <Route path="usuarios" element={<UsuariosPage />} />
        <Route path="usuarios/:id" element={<UsuarioDetailPage />} />
        <Route path="suscripciones" element={<SuscripcionesPage />} />
        <Route path="planes" element={<PlanesHubPage />} />
        <Route path="financiero" element={<FinancieroPage />} />
        <Route path="financiero/gastos/:id" element={<FinancieroGastoDetailPage />} />
        <Route path="financiero/proveedores/:id" element={<FinancieroProveedorDetailPage />} />
        <Route path="catalogo" element={<CatalogoPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="monitor" element={<MonitorPage />} />
        <Route path="monitor/alarmas/:id" element={<AlarmDetailPage />} />
        <Route path="almacenamiento" element={<AlmacenamientoPage />} />
        <Route path="traducciones" element={<TraduccionesPage />} />
        <Route path="email" element={<EmailPage />} />
        <Route path="configuracion" element={<ConfiguracionPage />} />
        <Route path="mi-cuenta" element={<MiCuentaPage />} />
        <Route path="desarrollo" element={<DesarrolloPage />} />
        <Route path="test" element={<TestPage />} />
        <Route path="procesos" element={<ProcesosPage />} />
        {/* Las direcciones viejas (/invitaciones, /estadisticas, /workers,
            /agente-local…) las lleva RootGate a la de hoy: lib/legacyRoutes.ts. */}
        <Route path="paises" element={<PaisesPage />} />
        <Route path="paises/:code" element={<PaisDetailPage />} />
        <Route path="impresoras" element={<ImpresorasPage />} />
        <Route path="versiones-agente" element={<VersionesAgentePage />} />
        <Route path="soporte" element={<SoportePage />} />
        <Route path="partners" element={<PartnersPage />} />
        <Route path="partners/:id" element={<PartnerDetailPage />} />
        {/* 404 real dentro del layout: un enlace roto se VE, en vez de caer al
            Dashboard en silencio como hacía el catch-all. */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Route>,
  ),
);

function App() {
  return (
    <ThemeProvider>
      <ViewportProvider>
        <ToastProvider>
          <ConfirmProvider>
            <RegionProvider>
              <AuthProvider>
                {/* Admin is Spanish-only — the provider uses its Spanish default copy. */}
                <UnsavedChangesProvider>
                  <RouterProvider router={router} />
                </UnsavedChangesProvider>
              </AuthProvider>
            </RegionProvider>
          </ConfirmProvider>
        </ToastProvider>
      </ViewportProvider>
    </ThemeProvider>
  );
}

export default App;
